import { useMemo, useRef } from "preact/hooks";

import { DataTable, type DataTableColumn } from "../components/DataTable";
import { InfoTooltip } from "../components/InfoTooltip";
import { PersonRosterCard } from "../components/PersonRosterCard";
import { TeamParentCard } from "../components/TeamParentCard";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { piecesForParent, tasksForPerson, type TeamPiece } from "../hooks/useTeamBoard";
import { UP_FOR_GRABS_LABEL } from "../lib/copy";
import { HELP_COPY } from "../lib/helpCopy";
import type { DueResponse, DueTask, TeamBoardOut } from "../types";

// Sprint-008 A3 (design-desktop-phase-a.md §3.5) — the desktop data-table
// applied to Team: the FLATTENED view of every `DueTask` across
// `teamDue`'s four groups (overdue/due_today/whenever/done_today), one
// row per task, sourced from the SAME already-fetched `teamDue` the
// phone roster (`PersonRosterCard`) reads — no second fetch. Group order
// mirrors the app's own established "overdue, due today, whenever, done
// today" ordering (`TodayView.tsx`).
type TeamGroup = "overdue" | "due_today" | "whenever" | "done_today";

interface TeamTableRow {
  task: DueTask;
  group: TeamGroup;
}

const TEAM_GROUP_LABEL: Record<TeamGroup, string> = {
  overdue: "Overdue",
  due_today: "Due today",
  whenever: "Whenever",
  done_today: "Done today",
};

// "Status (open first), then Due asc" (§3.5) — open groups sort before
// the done group, in the same overdue → due-today → whenever order the
// rest of the app already uses.
const TEAM_GROUP_ORDER: Record<TeamGroup, number> = {
  overdue: 0,
  due_today: 1,
  whenever: 2,
  done_today: 3,
};

function flattenTeamDue(teamDue: DueResponse | null): TeamTableRow[] {
  if (!teamDue) return [];
  const groups: Array<[TeamGroup, DueTask[]]> = [
    ["overdue", teamDue.overdue],
    ["due_today", teamDue.due_today],
    ["whenever", teamDue.whenever],
    ["done_today", teamDue.done_today],
  ];
  const rows: TeamTableRow[] = [];
  for (const [group, tasks] of groups) {
    for (const task of tasks) rows.push({ task, group });
  }
  return rows;
}

function dueSortValue(task: DueTask): number {
  return task.due_date ? new Date(task.due_date).getTime() : Number.POSITIVE_INFINITY;
}

function dueDisplayText(task: DueTask): string {
  return task.overdue_phrase ?? task.phrase;
}

function teamTableColumns(): Array<DataTableColumn<TeamTableRow>> {
  return [
    {
      key: "task",
      label: "Task",
      sortable: true,
      getSortValue: (row) => row.task.title,
      getCsvValue: (row) => row.task.title,
    },
    {
      key: "assignee",
      label: "Who's doing it",
      sortable: true,
      getSortValue: (row) => row.task.assignee_name ?? UP_FOR_GRABS_LABEL,
      getCsvValue: (row) => row.task.assignee_name ?? UP_FOR_GRABS_LABEL,
    },
    {
      key: "area",
      label: "Area",
      sortable: true,
      getSortValue: (row) => row.task.area ?? "",
      getCsvValue: (row) => row.task.area ?? "—",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      getSortValue: (row) => TEAM_GROUP_ORDER[row.group],
      getCsvValue: (row) => TEAM_GROUP_LABEL[row.group],
    },
    {
      key: "due",
      label: "Due",
      sortable: true,
      getSortValue: (row) => dueSortValue(row.task),
      getCsvValue: (row) => dueDisplayText(row.task),
    },
  ];
}

function defaultTeamCompare(a: TeamTableRow, b: TeamTableRow): number {
  const groupDiff = TEAM_GROUP_ORDER[a.group] - TEAM_GROUP_ORDER[b.group];
  if (groupDiff !== 0) return groupDiff;
  return dueSortValue(a.task) - dueSortValue(b.task);
}

const TEAM_FILTER_CHIPS = (["overdue", "due_today", "whenever", "done_today"] as TeamGroup[]).map(
  (group) => ({
    key: group,
    label: TEAM_GROUP_LABEL[group],
    predicate: (row: TeamTableRow) => row.group === group,
  }),
);

export interface TeamViewProps {
  board: TeamBoardOut;
  teamDue: DueResponse | null;
  meId: string;
  onTogglePiece: (piece: TeamPiece) => void;
  onMarkParentDone: (parentAssignmentId: string) => void;
  onUndoParentDone: (parentAssignmentId: string) => void;
}

/**
 * Team tab (design-spec-today-card.md §2/§3.7 + the sprint-003 A6 task
 * brief): visible only when `can_hand_out_work` — every delegated job's
 * "N of M done" card with its piece list ("who has what, done state"),
 * then — below `1024px` — the roster of per-person open/done-today
 * counts with a read-only expand. Never renders bids/financials (this
 * view has no code path that reads anything but `board`/`teamDue`, both
 * of which are already scoped server-side to never include
 * `projects`/`bids`).
 *
 * Sprint-008 A3 (design-desktop-phase-a.md §3.5) — at `≥1024px` the
 * "Everyone" roster (`PersonRosterCard` stack) is REPLACED by the shared
 * `DataTable`: the flattened Task/Assignee/Area/Status/Due view of every
 * `DueTask` across `teamDue`'s four groups. The "Handed-out jobs" cards
 * above stay unchanged at every width — they're a distinct write
 * interaction (mark-parent-done) that doesn't collapse cleanly into a
 * flat row (§3.5's own reasoning). **Flagged deviation**: the table's
 * primary (Task) cell is plain text, not a button — §3.5 never names a
 * per-row action for Team (unlike People/Chores, which explicitly call
 * out opening `PersonDetailOverlay`/`ChoreEditorOverlay`), and this
 * view's own established precedent (`PersonRosterCard`'s per-person
 * expand) is read-only by design: "a helper completes their own work
 * from their own Today, never from someone else looking at the Team
 * tab." `DataTable`'s `onRowActivate` is intentionally omitted here for
 * that reason — see the PR description.
 */
export function TeamView({
  board,
  teamDue,
  meId,
  onTogglePiece,
  onMarkParentDone,
  onUndoParentDone,
}: TeamViewProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // Sprint-008 A2 (R-A0-2) — route-change focus management, same governed
  // convention every other routed view in this sprint reuses (see
  // `TodayView.tsx`'s docstring for the full rationale).
  useFocusHeadingOnMount(headingRef);
  const isDesktop = useIsDesktop();

  const tableColumns = useMemo(() => teamTableColumns(), []);
  const tableRows = useMemo(() => flattenTeamDue(teamDue), [teamDue]);

  return (
    <section class="view" aria-label="Team">
      <h1 ref={headingRef} tabIndex={-1} class="view-heading">
        Team
      </h1>
      <p class="view-subheading">Who's doing what today.</p>

      {board.delegated_parents.length > 0 ? (
        <>
          <h2 class="section-label due">Handed-out jobs</h2>
          {board.delegated_parents.map((parent) => (
            <TeamParentCard
              key={parent.parent_assignment_id}
              parent={parent}
              pieces={piecesForParent(teamDue, parent.parent_assignment_id)}
              meId={meId}
              onTogglePiece={onTogglePiece}
              onMarkDone={() => onMarkParentDone(parent.parent_assignment_id)}
              onUndoDone={() => onUndoParentDone(parent.parent_assignment_id)}
            />
          ))}
        </>
      ) : null}

      {isDesktop ? (
        <>
          {/* Sprint-016 F3 (TM-01, ux-audit-whole-app-2026-07-11.md — S3)
           * — a first-time desktop admin, having just seen People/Chores
           * rows open a detail overlay on click (ADM-02), would reasonably
           * expect Team rows to do the same. This table is intentionally
           * read-only (a helper completes their own work from their own
           * Today, never from someone else looking at Team) — state that,
           * don't leave it silently absent. */}
          <p class="table-readonly-hint">Check off your own work from Today on your device.</p>
          <DataTable
            captionBase="Team"
            columns={tableColumns}
            rows={tableRows}
            getRowId={(row) => `${row.group}-${row.task.id}`}
            loadingMessage="Loading the team…"
            emptyMessage="Nothing handed out right now."
            primaryColumnKey="task"
            searchLabel="Search by task title"
            getSearchValue={(row) => row.task.title}
            filterChips={TEAM_FILTER_CHIPS}
            filterGroupLabel="Filter by status"
            defaultSortLabel="status ascending"
            defaultCompare={defaultTeamCompare}
            screenSlug="team"
          />
        </>
      ) : (
        <>
          <h2 class="section-label due">
            Everyone
            <InfoTooltip
              label={HELP_COPY.teamRosterReadOnly.label}
              content={HELP_COPY.teamRosterReadOnly.content}
            />
          </h2>
          {board.members.map((member) => (
            <PersonRosterCard
              key={member.user_id}
              member={member}
              tasks={tasksForPerson(teamDue, member.user_id)}
            />
          ))}
        </>
      )}
    </section>
  );
}
